import { useFlags } from '@atlaskit/flag';
import { SubLocationsApiService } from 'core/api';
import { ISubLocationDao } from 'core/api/types/sub-locations.interface';
import { useDialog } from 'core/providers/DialogProvider';
import { PageElement } from 'core/utilities/interface-helpers';
import SharedButton from 'shared/components/buttons/button';
import PageHeader from 'shared/components/page-header/page-header';
import SharedTable from 'shared/components/table/table';
import { ISharedTableColumn, ISharedTableCustomCellTemplate } from 'shared/components/table/table-interface';
import ConfirmationDialog from 'modules/dialogs/confirmation-dialog';
import { useCollection } from 'core/hooks/useCollection';
import { deleteItem, deleteItems } from 'core/utilities/api-wrapper';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Checkbox } from '@atlaskit/checkbox';
import Lozenge from '@atlaskit/lozenge';
import { QueryConstraint, where } from 'firebase/firestore';
import SharedFilters, { Filter, SelectedValue } from 'shared/components/filters/filters';
import ExportDialog from 'modules/dialogs/export-dialog';
import { IButton } from 'shared/components/buttons/button-interface';
import AddEditSubLocationDialog from './dialogs/add-edit-sub-location-dialog';
import { FirebaseFunctionNames } from 'core/constants/firebase-function-names';
import { useSharedData } from 'core/providers/SharedDataProvider';

const SubLocationsTableColumns: ISharedTableColumn[] = [
  { label: '', key: 'select', templateId: 'select', width: 1, isCompact: true },
  { label: 'Location', key: 'location', templateId: 'locationName', width: 10 },
  { label: 'Location Postcode', key: 'locationPostcode', templateId: 'locationPostcode', width: 10 },
  { label: 'Location Status', key: 'locationStatus', templateId: 'locationStatus', width: 10 },
  { label: 'Sub location', key: 'name', templateId: 'text', width: 15 },
  { label: 'Status', key: 'active', templateId: 'boolean', width: 10 },
  { label: '', key: 'action', templateId: 'action', width: 10 },
];

const SubLocationsList = () => {
  const dialog = useDialog();
  const flags = useFlags();

  const { locations } = useSharedData();

  const [filterQuery, setFilterQuery] = useState<SelectedValue[]>([]);
  const [constraints, setConstraints] = useState<QueryConstraint[]>([]);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  const { tableRows, loading, loadMore, hasMore, loadingMore } = useCollection<ISubLocationDao>({
    apiService: SubLocationsApiService,
    constraints,
  });

  useEffect(() => {
    setSelectedRows([]);
  }, [tableRows]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const newConstraints = filterQuery
        .filter((filter) => filter.value !== '' && !filter.id.includes('.'))
        .map((filter) => {
          const value = filter.value === 'true' ? true : filter.value === 'false' ? false : filter.value;
          return where(filter.id, '==', value);
        });

      setConstraints(newConstraints);
    }, 500);
    return () => clearTimeout(timer);
  }, [filterQuery]);

  const handleFilterChange = useCallback(
    (values: SelectedValue[]) => {
      const currentSerialized = JSON.stringify(filterQuery.sort((a, b) => a.id.localeCompare(b.id)));
      const newSerialized = JSON.stringify(values.sort((a, b) => a.id.localeCompare(b.id)));

      if (currentSerialized !== newSerialized) {
        setFilterQuery(values);
      }
    },
    [filterQuery]
  );

  const rowChecked = (event: ChangeEvent<HTMLInputElement>, uid: string) => {
    if (event.target.checked) {
      setSelectedRows((prevState) => [...prevState, uid]);
    } else {
      setSelectedRows((prevState) => prevState.filter((selectedUid) => selectedUid !== uid));
    }
  };

  const selectCellTemplate = (subLocation: ISubLocationDao) => {
    return (
      <Checkbox isChecked={selectedRows.includes(subLocation.uid)} onChange={(e) => rowChecked(e, subLocation.uid)} />
    );
  };

  const actionCellTemplate = (subLocation: ISubLocationDao) => (
    <div className='w-full flex justify-end space-x-2'>
      <SharedButton
        onClick={() => dialog?.openDialog(<AddEditSubLocationDialog subLocation={subLocation} />)}
        type='button'
        appearance='subtle'
        spacing='compact'
        label='Update'
      />
      <SharedButton
        onClick={() =>
          dialog?.openDialog(
            <ConfirmationDialog
              title='Delete sub location'
              textContent='Are you sure you want to delete this sub location? This action cannot be undone.'
              action={() => deleteItem(SubLocationsApiService, subLocation, flags, dialog)}
              buttonLabel='Delete Sub Location'
            />
          )
        }
        type='button'
        appearance='subtle'
        spacing='compact'
        label='Delete'
      />
    </div>
  );

  const customTemplates: ISharedTableCustomCellTemplate[] = [
    {
      template: actionCellTemplate,
      id: 'action',
    },
    {
      template: (subLocation: ISubLocationDao) => (
        <p>{locations?.find((location) => location.uid === subLocation.locationId)?.name || '-'}</p>
      ),
      id: 'locationName',
    },
    {
      template: (subLocation: ISubLocationDao) => (
        <p>
          {locations?.find((location) => location.uid === subLocation.locationId)?.address.fullAddress.postcode || '-'}
        </p>
      ),
      id: 'locationPostcode',
    },
    {
      template: (subLocation: ISubLocationDao) => (
        <Lozenge
          appearance={
            locations?.find((location) => location.uid === subLocation.locationId)?.active ? 'success' : 'removed'
          }
        >
          {locations?.find((location) => location.uid === subLocation.locationId)?.active ? 'Active' : 'Inactive'}
        </Lozenge>
      ),
      id: 'locationStatus',
    },
    { template: selectCellTemplate, id: 'select' },
  ];

  const headerActions: PageElement[] = [
    {
      key: 'exportSubLocations',
      element: (
        <SharedButton
          onClick={() =>
            dialog?.openDialog(
              <ExportDialog
                title='Export Sub Locations'
                apiService={SubLocationsApiService}
                functionName={FirebaseFunctionNames.EXPORT_SUB_LOCATIONS}
                filterQuery={filterQuery}
              />
            )
          }
          type='button'
          appearance='default'
          label='Export Sub Locations'
        />
      ),
    },
    ...(selectedRows.length
      ? [
          {
            key: 'deleteSubLocations',
            element: (
              <SharedButton
                onClick={() =>
                  dialog?.openDialog(
                    <ConfirmationDialog
                      title='Delete sub locations'
                      textContent='Are you sure you want to delete these sub locations? This action cannot be undone.'
                      action={() => deleteItems(SubLocationsApiService, selectedRows, flags, dialog)}
                      buttonLabel='Delete Sub Locations'
                    />
                  )
                }
                type='button'
                appearance='danger'
                label='Delete Vehicles'
              />
            ),
          },
        ]
      : []),
  ];

  const filters: Filter[] = [
    {
      id: 'locationId',
      label: 'Location',
      options: locations?.map((location) => ({ value: location.uid, label: location.name })) || [],
    },
    { id: 'name', label: 'Sub Location' },
    {
      id: 'active',
      label: 'Status',
      options: [
        { value: 'true', label: 'Active' },
        { value: 'false', label: 'Inactive' },
      ],
    },
  ];

  const loadMoreButton: IButton = {
    onClick: () => loadMore(),
    type: 'submit',
    appearance: 'subtle',
    label: 'Load more',
    isLoading: loadingMore,
  };

  return (
    <>
      <PageHeader title='Sub Locations' actions={headerActions} />
      <SharedFilters filters={filters} onFilterValueChange={handleFilterChange} />
      <SharedTable
        columns={SubLocationsTableColumns}
        customTemplates={customTemplates}
        rows={tableRows}
        isLoading={loading}
        loadMoreButton={hasMore ? loadMoreButton : undefined}
      />
    </>
  );
};

export default SubLocationsList;
